import { useCallback, useEffect, useState, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import type { AdminAuditEvent, AdminSubstudy } from "../../api/types";
import { ErrorNotice } from "../ErrorNotice";
import { SkeletonRows } from "../Skeleton";
import { TabPanel } from "../Tabs";
import { downloadCsv } from "./download";

// Every event type the backend records. Kept honest by
// tests/unit/test_audit_event_types.py, which parses THIS list and asserts it
// matches the audit.record() call sites exactly — the comment-only version of
// this contract silently drifted through a rename.
const AUDIT_EVENT_TYPES = [
  // auth
  "auth.registered",
  "auth.login",
  "auth.login_failed",
  "auth.account_locked",
  "auth.logout",
  "auth.token_refreshed",
  "auth.email_verified",
  "auth.password_changed",
  "auth.password_reset",
  "auth.email_changed",
  // participant actions
  "participant.consented",
  "consent.viewed",
  "questionnaire.submitted",
  "photo.uploaded",
  "deletion_request.created",
  // system (worker / sweeper / file serving)
  "photo.processed",
  "photo.rejected",
  "photo.requeued",
  "photo.archived",
  "photo.local_purged",
  "photo.downloaded",
  "blob.downloaded",
  // collaborator actions
  "batch.created",
  "batch.completed",
  "batch.photos_appended",
  "export.batch_results",
  "export.substudy_results",
  // admin actions
  "participant.roster_viewed",
  "participant.substudy_reassigned",
  "participant.enrollment_changed",
  "deletion_request.executed",
  "deletion_request.denied",
  "substudy.created",
  "access_codes.loaded",
  "access_codes.reset_link_issued",
  "collaborator.created",
  "collaborator.invite_resent",
  "protocol.created",
  "protocol.activated",
  "consent_version.created",
  "consent_version.activated",
  "export.substudy_questionnaires",
  "export.analysis",
  "export.photos_zip",
  "export.consents_zip",
  "export.audit",
];

const AUDIT_PAGE = 50;

// Audit log — queryable trail of admin actions (§5.5), newest first. Takes the
// shared substudy list to render substudy slugs on the rows.
export function AuditTab({ substudies }: { substudies: AdminSubstudy[] }): ReactElement {
  const [auditRows, setAuditRows] = useState<AdminAuditEvent[]>([]);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditType, setAuditType] = useState("");
  const [auditParticipant, setAuditParticipant] = useState("");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");

  // The one place the four filters become query params. The on-screen list and
  // the CSV export must ask for the same slice — the export's whole promise is
  // "everything matching these filters, not just the loaded page", and a second
  // copy of these conditions is how that promise quietly stops holding.
  const auditFilterParams = useCallback((): URLSearchParams => {
    const qs = new URLSearchParams();
    if (auditType) qs.set("event_type", auditType);
    if (auditParticipant.trim()) qs.set("participant_id", auditParticipant.trim());
    if (auditFrom) qs.set("occurred_from", auditFrom);
    if (auditTo) qs.set("occurred_to", auditTo);
    return qs;
  }, [auditType, auditParticipant, auditFrom, auditTo]);

  const loadAudit = useCallback(
    async (offset: number) => {
      try {
        const qs = auditFilterParams();
        qs.set("limit", String(AUDIT_PAGE));
        qs.set("offset", String(offset));
        const rows = await api<AdminAuditEvent[]>("GET", `/admin/audit?${qs}`);
        const page = Array.isArray(rows) ? rows : [];
        setAuditRows((prev) => (offset === 0 ? page : [...prev, ...page]));
        setAuditHasMore(page.length === AUDIT_PAGE);
        setAuditError(null);
      } catch (err) {
        setAuditError(err instanceof ApiError ? err.message : "Could not load the audit log.");
      } finally {
        setAuditLoaded(true);
      }
    },
    [auditFilterParams],
  );

  useEffect(() => {
    void loadAudit(0);
    // loadAudit changes with every filter keystroke; refetching is explicit
    // (Apply / Load more) so the mount effect must not re-run on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Full filtered export (not just the loaded page) via /admin/export/audit.csv.
  async function exportCsv(): Promise<void> {
    try {
      const query = auditFilterParams().toString();
      await downloadCsv(
        `/admin/export/audit.csv${query ? `?${query}` : ""}`,
        "faceage-audit.csv",
      );
      setAuditError(null);
    } catch (err) {
      setAuditError(err instanceof ApiError ? err.message : "Could not export the audit log.");
    }
  }

  return (
    <TabPanel id="audit" title="Audit Log">
      <p className="muted">
        Every admin action (corrections, deletions, substudy changes, PHI exports), newest
        first. Append-only: entries can never be edited or deleted.
      </p>
      <div className="field-grid">
        <label className="field">
          <span>Event type</span>
          <select value={auditType} onChange={(e) => setAuditType(e.target.value)}>
            <option value="">Any</option>
            {AUDIT_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Participant ID</span>
          <input
            type="number"
            min={1}
            value={auditParticipant}
            onChange={(e) => setAuditParticipant(e.target.value)}
          />
        </label>
        <label className="field">
          <span>From</span>
          <input type="date" value={auditFrom} onChange={(e) => setAuditFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>To</span>
          <input type="date" value={auditTo} onChange={(e) => setAuditTo(e.target.value)} />
        </label>
      </div>
      <p>
        <button className="btn-ghost btn-sm" onClick={() => void loadAudit(0)}>
          Apply filters
        </button>{" "}
        <button className="btn-ghost btn-sm" onClick={() => void exportCsv()}>
          Export CSV
        </button>
      </p>
      {!auditLoaded ? (
        <SkeletonRows rows={3} />
      ) : auditError && auditRows.length === 0 ? (
        <ErrorNotice>{auditError}</ErrorNotice>
      ) : auditRows.length === 0 ? (
        <p className="muted">No audit events match.</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Actor</th>
                  <th>Participant</th>
                  <th>Substudy</th>
                  <th>Details</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.occurred_at).toLocaleString()}</td>
                    <td>
                      <code>{r.event_type}</code>
                    </td>
                    <td>{r.actor_email ?? r.actor_user_id ?? "—"}</td>
                    <td>{r.participant_id ?? "—"}</td>
                    <td>
                      {r.substudy_id === null
                        ? "—"
                        : (substudies.find((t) => t.id === r.substudy_id)?.slug ?? r.substudy_id)}
                    </td>
                    <td>
                      {r.payload && Object.keys(r.payload).length > 0 ? (
                        <code>{JSON.stringify(r.payload)}</code>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{r.ip ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditHasMore ? (
            <p>
              <button className="btn-ghost btn-sm" onClick={() => void loadAudit(auditRows.length)}>
                Load more
              </button>
            </p>
          ) : null}
        </>
      )}
      {auditError && auditRows.length > 0 ? <p className="form-error">{auditError}</p> : null}
    </TabPanel>
  );
}
